import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtNum, fmtEuro } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import Avatar from "@/components/Avatar";
import { exportElementToPdf } from "@/lib/pdf";
import { ChevronLeft, ChevronRight, Sun, AlertTriangle, CheckCircle2, Clock, Plane, Users, Save, LayoutList, GanttChartSquare, FileDown, X } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayStr = () => fmtDate(new Date());
const shiftDate = (iso, days) => {
  const [y, m, dd] = iso.split("-").map(Number);
  const d = new Date(y, m - 1, dd);
  d.setDate(d.getDate() + days);
  return fmtDate(d);
};
const DAYS = [["1", "Δευ"], ["2", "Τρί"], ["3", "Τετ"], ["4", "Πέμ"], ["5", "Παρ"], ["6", "Σάβ"], ["0", "Κυρ"]];
const MONTHS = ["Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος",
  "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος"];
const YEARS = (() => {
  const y = new Date().getFullYear();
  return Array.from({ length: 7 }, (_, i) => y - 3 + i);
})();
const selectCls = "rounded-xl border border-[#E3DED4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#45423C]";

const STATUS = {
  in: { label: "Σε βάρδια", cls: "bg-[#EEF0EA] text-[#6E7A63]", Icon: CheckCircle2 },
  done: { label: "Ολοκληρώθηκε", cls: "bg-[#F1EDE6] text-[#6F6A60]", Icon: CheckCircle2 },
  scheduled: { label: "Αναμένεται", cls: "bg-[#FFFBEB] text-[#92400E]", Icon: Clock },
  missed_in: { label: "Ξεχασμένη έναρξη", cls: "bg-[#FEF2F2] text-[#991B1B]", Icon: AlertTriangle },
  missed_out: { label: "Ξεχασμένη λήξη", cls: "bg-[#FEF2F2] text-[#991B1B]", Icon: AlertTriangle },
};
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" }) : "—");
const toMin = (t) => {
  const [h, m] = String(t || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};
const BAR_COLORS = ["#2F6B3A", "#1E7A78", "#5B3FA6", "#C86235", "#8A6A1F", "#7A2E4A", "#3C5E9A", "#6E7A63"];
const deptColor = (dept, all) => BAR_COLORS[Math.max(all.indexOf(dept), 0) % BAR_COLORS.length];
const minToHHMM = (min) => `${pad(Math.floor(((min % 1440) + 1440) % 1440 / 60))}:${pad(min % 60)}`;
const SNAP = 15;

export default function DayRoster() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const [day, setDay] = useState(todayStr());
  const [showCoverage, setShowCoverage] = useState(false);
  const [view, setView] = useState("timeline");
  const [rules, setRules] = useState(null);
  const [y, m, d] = day.split("-").map(Number);

  const { data, isLoading } = useQuery({
    queryKey: ["day-roster", day],
    queryFn: async () => (await api.get(`/schedules/day?day=${day}`)).data,
    refetchInterval: 60000,
  });
  const { data: coverage } = useQuery({
    queryKey: ["coverage"],
    queryFn: async () => (await api.get("/coverage")).data,
  });
  useEffect(() => { if (coverage) setRules(coverage); }, [coverage]);

  const saveRules = useMutation({
    mutationFn: async () => (await api.put("/coverage", rules)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["coverage"] });
      qc.invalidateQueries({ queryKey: ["day-roster"] });
      toast.success("Οι κανόνες κάλυψης αποθηκεύτηκαν");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const setRule = (dept, dw, v) => setRules((p) => p.map((r) =>
    r.department === dept ? { ...r, days: { ...r.days, [dw]: Math.max(Number(v) || 0, 0) } } : r));

  const saveShift = useMutation({
    mutationFn: async (body) => (await api.put("/schedules/day-shift", { day, ...body })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["day-roster"] });
      qc.invalidateQueries({ queryKey: ["schedule-week"] });
      toast.success("Η βάρδια ενημερώθηκε");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const [edit, setEdit] = useState(null);
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const pdfRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const exportPdf = async () => {
    if (!pdfRef.current) return;
    setPdfBusy(true);
    try {
      await exportElementToPdf(pdfRef.current, `TAF_Programma_${day}.pdf`, "landscape");
    } catch (e) {
      toast.error("Η εξαγωγή PDF απέτυχε");
    } finally {
      setPdfBusy(false);
    }
  };

  const setPart = (part, value) => {
    const yy = part === "y" ? Number(value) : y;
    const mm = part === "m" ? Number(value) : m;
    const dd = part === "d" ? Number(value) : d;
    const last = new Date(yy, mm, 0).getDate();
    setDay(`${yy}-${pad(mm)}-${pad(Math.min(dd, last))}`);
  };
  const daysInMonth = new Date(y, m, 0).getDate();

  const working = data?.working || [];
  const t = data?.totals || {};
  const allDepts = useMemo(
    () => [...new Set(working.map((w) => w.department))].sort((a, b) => a.localeCompare(b, "el")),
    [working]);
  const { startH, endH, hours } = useMemo(() => {
    const starts = working.map((w) => toMin(w.start));
    const ends = working.map((w) => toMin(w.end));
    const s = starts.length ? Math.min(Math.floor(Math.min(...starts) / 60), 8) : 6;
    const e = ends.length ? Math.max(Math.ceil(Math.max(...ends) / 60), 18) : 22;
    return { startH: s, endH: e, hours: Array.from({ length: e - s + 1 }, (_, i) => s + i) };
  }, [working]);
  const span = (endH - startH) * 60 || 1;
  const pct = (min) => `${Math.min(Math.max(((min - startH * 60) / span) * 100, 0), 100)}%`;

  const onBarDown = (e, w, mode = "move") => {
    if (!canEdit) return;
    e.stopPropagation();
    const bar = mode === "move" ? e.currentTarget : e.currentTarget.parentElement;
    const track = bar.parentElement;
    dragRef.current = { name: w.employee_name, mode, x0: e.clientX, width: track.getBoundingClientRect().width,
                        s0: toMin(w.start), e0: toMin(w.end), moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onBarMove = (e) => {
    const g = dragRef.current;
    if (!g) return;
    const deltaMin = Math.round(((e.clientX - g.x0) / g.width) * span / SNAP) * SNAP;
    if (!deltaMin && !g.moved) return;
    g.moved = true;
    if (g.mode === "start") setDrag({ name: g.name, start: Math.min(g.s0 + deltaMin, g.e0 - SNAP), end: g.e0 });
    else if (g.mode === "end") setDrag({ name: g.name, start: g.s0, end: Math.max(g.e0 + deltaMin, g.s0 + SNAP) });
    else setDrag({ name: g.name, start: g.s0 + deltaMin, end: g.e0 + deltaMin });
  };
  const onBarUp = (w) => {
    const g = dragRef.current;
    dragRef.current = null;
    if (g?.moved && drag) {
      saveShift.mutate({ employee_name: w.employee_name, start: minToHHMM(drag.start), end: minToHHMM(drag.end) });
      setDrag(null);
      return;
    }
    setDrag(null);
    if (canEdit && g) setEdit({ employee_name: w.employee_name, start: w.start, end: w.end });
  };
  const onTrackClick = (e, name) => {
    if (!canEdit || dragRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const raw = startH * 60 + ((e.clientX - rect.left) / rect.width) * span;
    const s = Math.max(Math.round(raw / 30) * 30, 0);
    setEdit({ employee_name: name, start: minToHHMM(s), end: minToHHMM(Math.min(s + 480, 1425)) });
  };
  const barRange = (w) => (drag?.name === w.employee_name
    ? { s: drag.start, e: drag.end } : { s: toMin(w.start), e: toMin(w.end) });

  const byDept = working.reduce((acc, w) => {
    (acc[w.department] = acc[w.department] || []).push(w);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="day-roster-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Βάρδια Ημέρας</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">
            {data?.is_today ? "Ποιοι δουλεύουν σήμερα" : `Βάρδιες ${data?.weekday || ""}`}
          </h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            {data?.weekday} {d} {MONTHS[m - 1]} {y} · {fmtNum(t.hours)} ώρες · {fmtEuro(t.cost)} κόστος εργασίας
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="day-prev" onClick={() => setDay(shiftDate(day, -1))}
            className="h-10 w-10 rounded-xl border border-[#E3DED4] bg-white flex items-center justify-center hover:border-[#B9AE9A]">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <select data-testid="day-day-select" value={d} onChange={(e) => setPart("d", e.target.value)} className={selectCls}>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <select data-testid="day-month-select" value={m} onChange={(e) => setPart("m", e.target.value)} className={selectCls}>
            {MONTHS.map((lbl, i) => <option key={lbl} value={i + 1}>{lbl}</option>)}
          </select>
          <select data-testid="day-year-select" value={y} onChange={(e) => setPart("y", e.target.value)} className={selectCls}>
            {YEARS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button data-testid="day-next" onClick={() => setDay(shiftDate(day, 1))}
            className="h-10 w-10 rounded-xl border border-[#E3DED4] bg-white flex items-center justify-center hover:border-[#B9AE9A]">
            <ChevronRight className="h-4 w-4" />
          </button>
          <button data-testid="day-today" onClick={() => setDay(todayStr())}
            className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2.5 text-sm font-semibold hover:border-[#B9AE9A]">
            Σήμερα
          </button>
          <button data-testid="day-pdf" onClick={exportPdf} disabled={pdfBusy}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-3 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
            <FileDown className="h-4 w-4" /> {pdfBusy ? "Δημιουργία…" : "PDF"}
          </button>
          <button data-testid="day-view-toggle" onClick={() => setView((v) => (v === "timeline" ? "list" : "timeline"))}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-3 py-2.5 text-sm font-semibold text-[#45423C] hover:border-[#B9AE9A]">
            {view === "timeline" ? <LayoutList className="h-4 w-4" /> : <GanttChartSquare className="h-4 w-4" />}
            {view === "timeline" ? "Λίστα" : "Χρονοδιάγραμμα"}
          </button>
          <button data-testid="coverage-toggle" onClick={() => setShowCoverage((v) => !v)}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-3 py-2.5 text-sm font-semibold text-[#6E7A63] hover:border-[#6E7A63]">
            <Users className="h-4 w-4" /> Κάλυψη
          </button>
        </div>
      </div>

      {showCoverage && rules && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="coverage-settings">
          <div className="px-5 py-3 border-b border-[#E3DED4] flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-medium text-[#45423C]">Ελάχιστη κάλυψη ανά τμήμα & ημέρα</h2>
              <p className="text-xs text-[#6F6A60] mt-0.5">0 = χωρίς απαίτηση. Αν λείπει κάλυψη, ειδοποιούνται Ιδιοκτήτης, HR και Διευθυντές.</p>
            </div>
            {canEdit && (
              <button data-testid="coverage-save" onClick={() => saveRules.mutate()} disabled={saveRules.isPending}
                className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
                <Save className="h-4 w-4" /> Αποθήκευση
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-4 py-2.5">Τμήμα</th>
                  {DAYS.map(([dw, l]) => <th key={dw} className="px-2 py-2.5 text-center">{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.department} data-testid={`coverage-row-${r.department}`} className="border-t border-[#E3DED4]">
                    <td className="px-4 py-1.5 font-medium text-[#45423C]">{r.department}</td>
                    {DAYS.map(([dw]) => (
                      <td key={dw} className="px-2 py-1.5 text-center">
                        <input data-testid={`coverage-${r.department}-${dw}`} type="number" min="0" disabled={!canEdit}
                          value={r.days?.[dw] ?? 0} onChange={(e) => setRule(r.department, dw, e.target.value)}
                          className="w-14 rounded border border-[#E3DED4] px-2 py-1 text-center text-sm tabular outline-none focus:border-[#45423C] disabled:bg-[#F7F5F1]" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[["Σε βάρδια", fmtNum(t.working)], ["Ώρες ημέρας", fmtNum(t.hours)], ["Κόστος ημέρας", fmtEuro(t.cost)],
          ["Ρεπό / Άδεια", `${fmtNum(t.off)} / ${fmtNum(t.on_leave)}`], ["Λείπουν άτομα", fmtNum(t.coverage_missing)]].map(([l, v], i) => (
          <div key={l} data-testid={`day-kpi-${l}`}
            className={`min-w-0 rounded-2xl p-5 ${i === 4 && t.coverage_missing > 0 ? "bg-[#991B1B] text-white" : "bg-white border border-[#E3DED4]"}`}>
            <div className={`text-[10px] uppercase tracking-widest break-words ${i === 4 && t.coverage_missing > 0 ? "text-white/70" : "text-[#6F6A60]"}`}>{l}</div>
            <div className="font-serif text-2xl mt-2 tabular">{v}</div>
          </div>
        ))}
      </div>

      {data?.coverage_gaps?.length > 0 && (
        <div className="rounded-2xl border border-[#991B1B]/30 bg-[#FEF2F2] p-5 space-y-2" data-testid="coverage-alert">
          <div className="flex items-center gap-2 text-[#991B1B]">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-semibold">Ελλιπής κάλυψη σε {data.coverage_gaps.length} τμήματα</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.coverage_gaps.map((g) => (
              <span key={g.department} data-testid={`coverage-gap-${g.department}`}
                className="rounded-full bg-white border border-[#991B1B]/20 px-3 py-1 text-xs font-semibold text-[#991B1B] tabular">
                {g.department}: {g.actual}/{g.required} — λείπουν {g.missing}
              </span>
            ))}
          </div>
        </div>
      )}

      {isLoading && <div className="text-sm text-[#6F6A60]">Φόρτωση…</div>}

      {view === "timeline" && (
        <div ref={pdfRef} className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="day-timeline">
          <div className="px-5 py-3 border-b border-[#E3DED4] flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-medium text-[#45423C]">Ημερήσιο πρόγραμμα · {d}/{pad(m)}/{y} ({data?.weekday})</h2>
            <span className="text-xs text-[#6F6A60] tabular">
              {working.length} άτομα · {fmtNum(t.hours)} ώρες{canEdit ? " · κλικ ή σύρσιμο στη μπάρα για αλλαγή ώρας" : ""}
            </span>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              <div className="flex items-center border-b border-[#E3DED4] bg-[#F7F5F1]">
                <div className="w-[240px] shrink-0 px-5 py-2.5 text-[10px] uppercase tracking-widest text-[#6F6A60]">
                  Προσωπικό ({working.length})
                </div>
                <div className="flex-1 flex">
                  {hours.slice(0, -1).map((h) => (
                    <div key={h} className="flex-1 border-l border-[#E3DED4] px-2 py-2.5 text-[11px] text-[#6F6A60] tabular">
                      {pad(h)}:00
                    </div>
                  ))}
                </div>
              </div>
              {(data?.coverage_gaps || []).map((g) => (
                <div key={`gap-${g.department}`} data-testid={`open-shift-row-${g.department}`}
                  className="flex items-stretch border-b border-[#E3DED4] bg-[#FEF7F4]">
                  <div className="w-[240px] shrink-0 px-5 py-3 flex items-center gap-3 min-w-0">
                    <span className="h-8 w-8 rounded-full bg-white border border-dashed border-[#C86235] inline-flex items-center justify-center shrink-0">
                      <AlertTriangle className="h-3.5 w-3.5 text-[#C86235]" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#a24a24] truncate">Ανοιχτές βάρδιες</div>
                      <div className="text-[11px] text-[#6F6A60] truncate">{g.department} · λείπουν {g.missing} από {g.required}</div>
                    </div>
                  </div>
                  <div className="flex-1 relative min-h-[56px]">
                    <div className="absolute inset-0 flex pointer-events-none">
                      {hours.slice(0, -1).map((h) => <div key={h} className="flex-1 border-l border-[#EFEAE1]" />)}
                    </div>
                    <div data-testid={`open-shift-bar-${g.department}`}
                      className="absolute top-2.5 h-[36px] rounded-lg px-2.5 py-1 border-2 border-dashed border-[#C86235] bg-[#C86235]/10 overflow-hidden"
                      style={{ left: pct(toMin(g.start)), width: `calc(${pct(toMin(g.end))} - ${pct(toMin(g.start))})` }}>
                      <div className="text-[11px] font-semibold leading-tight tabular truncate text-[#a24a24]">{g.start}–{g.end}</div>
                      <div className="text-[10px] leading-tight truncate text-[#a24a24]">{g.department} · κενή θέση ×{g.missing}</div>
                    </div>
                  </div>
                </div>
              ))}
              {working.map((w) => {
                const st = STATUS[w.status] || STATUS.scheduled;
                const color = deptColor(w.department, allDepts);
                const r = barRange(w);
                return (
                  <div key={w.employee_name} data-testid={`timeline-row-${w.employee_name}`}
                    className="flex items-stretch border-b border-[#E3DED4] last:border-0 hover:bg-[#FAF9F6]">
                    <div className="w-[240px] shrink-0 px-5 py-3 flex items-center gap-3 min-w-0">
                      <Avatar name={w.employee_name} fileId={w.photo_file_id} size="md" testid={`timeline-avatar-${w.employee_name}`} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#45423C] truncate">{w.employee_name}</div>
                        <div className="text-[11px] text-[#6F6A60] tabular">
                          {fmtNum(w.hours)} ώρες · {fmtEuro(w.cost)}
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 relative min-h-[56px]" onClick={(e) => onTrackClick(e, w.employee_name)}>
                      <div className="absolute inset-0 flex pointer-events-none">
                        {hours.slice(0, -1).map((h) => <div key={h} className="flex-1 border-l border-[#EFEAE1]" />)}
                      </div>
                      <div data-testid={`timeline-bar-${w.employee_name}`}
                        title={`${w.start}–${w.end} · ${w.position || w.department} · ${st.label}`}
                        onPointerDown={(e) => onBarDown(e, w)}
                        onPointerMove={onBarMove}
                        onPointerUp={() => onBarUp(w)}
                        className={`absolute top-2.5 h-[36px] rounded-lg px-2.5 py-1 text-white overflow-hidden shadow-sm select-none ${canEdit ? "cursor-grab active:cursor-grabbing hover:brightness-110" : ""}`}
                        style={{ left: pct(r.s), width: `calc(${pct(r.e)} - ${pct(r.s)})`, backgroundColor: color, touchAction: "none" }}>
                        <div className="text-[11px] font-semibold leading-tight tabular truncate">{minToHHMM(r.s)}–{minToHHMM(r.e)}</div>
                        <div className="text-[10px] leading-tight truncate opacity-90">{w.position || w.department}</div>
                        {canEdit && (
                          <>
                            <div data-testid={`resize-start-${w.employee_name}`} title="Αλλαγή έναρξης"
                              onPointerDown={(e) => onBarDown(e, w, "start")} onPointerMove={onBarMove} onPointerUp={() => onBarUp(w)}
                              className="absolute left-0 top-0 h-full w-2.5 cursor-ew-resize bg-white/25 hover:bg-white/50"
                              style={{ touchAction: "none" }} />
                            <div data-testid={`resize-end-${w.employee_name}`} title="Αλλαγή λήξης"
                              onPointerDown={(e) => onBarDown(e, w, "end")} onPointerMove={onBarMove} onPointerUp={() => onBarUp(w)}
                              className="absolute right-0 top-0 h-full w-2.5 cursor-ew-resize bg-white/25 hover:bg-white/50"
                              style={{ touchAction: "none" }} />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {(data?.off || []).map((o) => (
                <div key={`off-${o.employee_name}`} data-testid={`timeline-off-row-${o.employee_name}`}
                  className="flex items-stretch border-b border-[#E3DED4] last:border-0 bg-[#FBFAF7] hover:bg-[#F7F5F1]">
                  <div className="w-[240px] shrink-0 px-5 py-3 flex items-center gap-3 min-w-0">
                    <Avatar name={o.employee_name} fileId={o.photo_file_id} size="md" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#6F6A60] truncate">{o.employee_name}</div>
                      <div className="text-[11px] text-[#8A8577] truncate">
                        Ρεπό{canEdit ? " · κλικ για νέα βάρδια" : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 relative min-h-[56px]" data-testid={`timeline-empty-track-${o.employee_name}`}
                    onClick={(e) => onTrackClick(e, o.employee_name)}>
                    <div className="absolute inset-0 flex pointer-events-none">
                      {hours.slice(0, -1).map((h) => <div key={h} className="flex-1 border-l border-[#EFEAE1]" />)}
                    </div>
                    {canEdit && (
                      <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[#B9AE9A] opacity-0 hover:opacity-100 transition-opacity">
                        + Προσθήκη βάρδιας
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {!isLoading && working.length === 0 && (
                <div className="p-8 text-center text-[#6F6A60]">Καμία προγραμματισμένη βάρδια για αυτή την ημέρα.</div>
              )}
            </div>
          </div>
          {allDepts.length > 0 && (
            <div className="px-5 py-3 border-t border-[#E3DED4] flex flex-wrap gap-3" data-testid="timeline-legend">
              {allDepts.map((dp) => (
                <span key={dp} className="inline-flex items-center gap-1.5 text-[11px] text-[#6F6A60]">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: deptColor(dp, allDepts) }} />
                  {dp}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "list" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {Object.entries(byDept).map(([dept, rows]) => {
            const summary = (data?.departments || []).find((x) => x.department === dept) || {};
            return (
              <div key={dept} className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid={`day-dept-${dept}`}>
                <div className="px-5 py-3 border-b border-[#E3DED4] flex items-baseline justify-between gap-3">
                  <h2 className="font-medium text-[#45423C] flex items-center gap-2">
                    <Sun className="h-4 w-4 text-[#8A8577]" /> {dept}
                  </h2>
                  <span className="text-xs text-[#6F6A60] tabular">
                    {rows.length}{summary.required ? `/${summary.required}` : ""} άτομα · {fmtNum(summary.hours)} ώρες · {summary.first_start}–{summary.last_end}
                    {summary.missing > 0 && <span className="ml-2 font-semibold text-[#991B1B]">λείπουν {summary.missing}</span>}
                  </span>
                </div>
                <div className="divide-y divide-[#E3DED4]">
                  {rows.map((w) => {
                    const st = STATUS[w.status] || STATUS.scheduled;
                    const Icon = st.Icon;
                    return (
                      <div key={w.employee_name} data-testid={`day-row-${w.employee_name}`}
                        className="px-5 py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar name={w.employee_name} fileId={w.photo_file_id} size="md" />
                          <div className="min-w-0">
                            <div className="font-medium text-[#45423C] text-sm truncate">{w.employee_name}</div>
                            <div className="text-[11px] text-[#6F6A60]">
                              {w.position || dept} · έναρξη {hhmm(w.clock_in)} · λήξη {hhmm(w.clock_out)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-semibold tabular text-[#45423C]">{w.start}–{w.end}</div>
                          <span className={`inline-flex items-center gap-1 mt-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}>
                            <Icon className="h-3 w-3" /> {st.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {!isLoading && Object.keys(byDept).length === 0 && (
            <div className="rounded-2xl bg-white border border-[#E3DED4] p-8 text-center text-[#6F6A60]">
              Καμία προγραμματισμένη βάρδια για αυτή την ημέρα.
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {[["Σε άδεια", data?.on_leave, Plane], ["Ρεπό", data?.off, Sun]].map(([title, rows, Icon]) => (
          <div key={title} className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid={`day-list-${title}`}>
            <div className="px-5 py-3 border-b border-[#E3DED4] flex items-center gap-2">
              <Icon className="h-4 w-4 text-[#6F6A60]" />
              <h2 className="font-medium text-[#45423C]">{title} ({rows?.length || 0})</h2>
            </div>
            <div className="p-5 flex flex-wrap gap-2">
              {(rows || []).map((r) => (
                <span key={r.employee_name} className="rounded-full bg-[#F1EDE6] px-3 py-1 text-xs text-[#6F6A60]">
                  {r.employee_name}
                </span>
              ))}
              {!rows?.length && <span className="text-sm text-[#6F6A60]">Κανένας.</span>}
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="shift-edit-modal"
          onClick={() => setEdit(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-serif text-xl text-[#45423C]">{edit.employee_name}</h2>
                <p className="text-xs text-[#6F6A60] mt-0.5">{d} {MONTHS[m - 1]} {y} · {data?.weekday}</p>
              </div>
              <button data-testid="shift-edit-close" onClick={() => setEdit(null)}
                className="h-8 w-8 rounded-lg border border-[#E3DED4] flex items-center justify-center hover:border-[#B9AE9A]">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-[#6F6A60]">Έναρξη</span>
                <input data-testid="shift-edit-start" type="time" step="900" value={edit.start}
                  onChange={(e) => setEdit({ ...edit, start: e.target.value })}
                  className="w-full rounded-xl border border-[#E3DED4] px-3 py-2.5 text-sm outline-none focus:border-[#45423C]" />
              </label>
              <label className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-[#6F6A60]">Λήξη</span>
                <input data-testid="shift-edit-end" type="time" step="900" value={edit.end}
                  onChange={(e) => setEdit({ ...edit, end: e.target.value })}
                  className="w-full rounded-xl border border-[#E3DED4] px-3 py-2.5 text-sm outline-none focus:border-[#45423C]" />
              </label>
            </div>
            <div className="flex items-center justify-between gap-3 pt-1">
              <button data-testid="shift-edit-off" disabled={saveShift.isPending}
                onClick={() => { saveShift.mutate({ employee_name: edit.employee_name, off: true }); setEdit(null); }}
                className="rounded-xl border border-[#E3DED4] px-3 py-2.5 text-sm font-semibold text-[#6F6A60] hover:border-[#991B1B] hover:text-[#991B1B] disabled:opacity-50">
                Ρεπό
              </button>
              <button data-testid="shift-edit-save" disabled={saveShift.isPending}
                onClick={() => { saveShift.mutate({ employee_name: edit.employee_name, start: edit.start, end: edit.end }); setEdit(null); }}
                className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
                <Save className="h-4 w-4" /> Αποθήκευση
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
